import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Users, Rocket, ArrowRight, Award, Phone, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoachesPage = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const response = await axios.get(`${API}/coaches/`);
        setCoaches(response.data);
      } catch (err) {
        console.error("Failed to fetch coaches:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCoaches();
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const hasCoaches = !loading && coaches.length > 0;

  return (
    <div className="min-h-screen bg-[#05050A]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 relative" data-testid="coaches-hero">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/00dc70e4-70e9-471a-a527-cf5ef1f5c6b5/images/f2cd200b3db4fc136cc5daabd7828ff2fb8403cc5bd88569b0aeef2fd67a6f97.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            {...fadeInUp}
          >
            <span className="text-[#0066FF] text-sm font-bold uppercase tracking-[0.2em] mb-4 block">
              Équipe APM
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter mb-6">
              Nos Coachs<br />
              <span className="text-[#0066FF]">Affiliés</span>
            </h1>
            <p className="text-[#94A3B8] text-lg leading-relaxed">
              Découvrez nos personal trainers certifiés, prêts à vous accompagner
              dans votre transformation physique.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Coaches List or Coming Soon */}
      <section className="section-padding" data-testid="coaches-list-section">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : hasCoaches ? (
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              {coaches.map((coach, i) => (
                <motion.div
                  key={coach.id}
                  className="bg-[#0F0F16] border border-white/10 overflow-hidden hover:border-[#0066FF]/50 transition-colors group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  data-testid={`coach-card-${coach.id}`}
                >
                  <div className="aspect-square overflow-hidden bg-[#05050A]">
                    <img
                      src={coach.photo_url || "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600"}
                      alt={coach.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-['Outfit'] text-xl font-bold uppercase mb-1">{coach.name}</h3>
                    <p className="text-[#0066FF] text-sm font-semibold mb-3">{coach.specialty}</p>
                    <p className="text-[#94A3B8] text-sm mb-4 line-clamp-3 leading-relaxed">{coach.bio}</p>
                    <div className="space-y-2 pt-4 border-t border-white/10 text-xs">
                      <div className="flex items-center gap-2 text-[#94A3B8]">
                        <Award className="w-3.5 h-3.5 text-[#0066FF] flex-shrink-0" />
                        <span className="truncate">{coach.diploma}</span>
                      </div>
                      {coach.rcpro_number && (
                        <div className="flex items-center gap-2 text-[#94A3B8]">
                          <span className="text-[#0066FF] font-bold text-[10px] uppercase tracking-wider">RC Pro</span>
                          <span className="text-white">N° {coach.rcpro_number}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-[#94A3B8]">
                        <Phone className="w-3.5 h-3.5 text-[#0066FF] flex-shrink-0" />
                        <a href={`tel:${coach.phone}`} className="hover:text-white truncate">{coach.phone}</a>
                      </div>
                      <div className="flex items-center gap-2 text-[#94A3B8]">
                        <Mail className="w-3.5 h-3.5 text-[#0066FF] flex-shrink-0" />
                        <a href={`mailto:${coach.email}`} className="hover:text-white truncate">{coach.email}</a>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              className="max-w-2xl mx-auto text-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              data-testid="coaches-coming-soon"
            >
              <div className="bg-[#0F0F16] border border-white/10 p-12 md:p-16">
                <div className="w-20 h-20 bg-[#0066FF]/10 border border-[#0066FF]/30 flex items-center justify-center mx-auto mb-8">
                  <Users className="w-10 h-10 text-[#0066FF]" />
                </div>

                <h2 className="font-['Outfit'] text-2xl md:text-3xl font-bold uppercase mb-4" data-testid="coming-soon-title">
                  Bientôt disponible
                </h2>

                <p className="text-[#94A3B8] leading-relaxed mb-8 max-w-lg mx-auto">
                  APM est un projet ambitieux qui démarre en <span className="text-[#0066FF] font-semibold">2026</span>.
                  Nos premiers coachs affiliés seront bientôt présentés ici.
                  En attendant, pourquoi ne pas faire partie des pionniers ?
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
                    <Rocket className="w-5 h-5 text-[#0066FF]" />
                    <span>Lancement 2026</span>
                  </div>
                  <div className="hidden sm:block w-px h-4 bg-white/20"></div>
                  <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
                    <span>3 salles Basic-Fit partenaires</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA - Rejoindre */}
      <section className="section-padding bg-[#0F0F16]" data-testid="coaches-cta">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-2xl mx-auto"
            {...fadeInUp}
            viewport={{ once: true }}
          >
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold uppercase tracking-tight mb-6">
              Vous êtes coach sportif ?
            </h2>
            <p className="text-[#94A3B8] mb-8">
              Rejoignez APM dès maintenant et faites partie des premiers coachs affiliés.
              Ensemble, construisons votre réussite vers <span className="text-[#0066FF]">3000€/mois en 90 jours</span>.
            </p>
            <Link
              to="/apply"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="coaches-cta-apply"
            >
              Devenir coach APM
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CoachesPage;
